import type { PreviewManifest } from './types'

export async function fetchJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, { signal })
  const value = await response.json()
  if (!response.ok) throw new Error(value.error ?? `Request failed (${response.status})`)
  return value as T
}

type StoredManifest = Omit<PreviewManifest, 'sheets'> & {
  sheets: Omit<PreviewManifest['sheets'][number], 'url'>[]
}

export function resolvePreviewManifest(
  raw: StoredManifest,
  manifestUrl: string,
  recordingId: string,
): PreviewManifest {
  const positive = (value: number) => Number.isSafeInteger(value) && value > 0
  if (
    raw.recording_id !== recordingId ||
    !positive(raw.tile_width) ||
    !positive(raw.tile_height) ||
    !positive(raw.columns) ||
    !positive(raw.rows) ||
    !Array.isArray(raw.sheets) ||
    !raw.sheets.length ||
    !Array.isArray(raw.samples) ||
    !raw.samples.length ||
    raw.samples.length > 100000
  ) {
    throw new Error('Invalid preview manifest')
  }
  const url = new URL(manifestUrl)
  if (!['https:', 'http:'].includes(url.protocol)) throw new Error('Invalid preview URL')
  const sheets = raw.sheets.map((sheet) => {
    if (
      !/^sheet-\d{5}\.webp$/.test(sheet.file) ||
      sheet.width !== raw.tile_width * raw.columns ||
      sheet.height !== raw.tile_height * raw.rows ||
      !positive(sheet.frame_count) ||
      sheet.frame_count > raw.columns * raw.rows
    ) {
      throw new Error('Invalid preview sheet')
    }
    return { ...sheet, url: new URL(sheet.file, url).href }
  })
  let previous = -Infinity
  for (const sample of raw.samples) {
    if (
      !Number.isFinite(sample.media_ms) ||
      sample.media_ms < 0 ||
      sample.media_ms <= previous ||
      !Number.isInteger(sample.sheet) ||
      !Number.isInteger(sample.tile) ||
      sample.tile < 0 ||
      !sheets[sample.sheet] ||
      sample.tile >= sheets[sample.sheet].frame_count
    )
      throw new Error('Invalid preview sample')
    previous = sample.media_ms
  }
  return { ...raw, sheets }
}

export async function fetchPreview(
  url: string,
  recordingId: string,
  signal?: AbortSignal,
): Promise<PreviewManifest> {
  const response = await fetch(url, { signal, credentials: 'omit' })
  if (!response.ok) {
    await response.body?.cancel()
    throw new Error(`Preview request failed (${response.status})`)
  }
  // Compressed responses can omit Content-Length; an absent header is not a zero-byte object.
  const length = response.headers.get('content-length')
  if (length !== null && Number(length) > 8 * 1024 * 1024) {
    await response.body?.cancel()
    throw new Error('Preview manifest is too large')
  }
  return resolvePreviewManifest(
    (await response.json()) as StoredManifest,
    url,
    recordingId,
  )
}
